import { useCallback, useState } from 'react';

import type { User, UserStatsSummary } from '../types/users.types';
import { usersApi, type UsersListPage, type UsersListQuery } from '../api/users.api';
import { rewardsApi } from '../../rewards/api/rewards.api';
import { buildIdempotencyKey } from '../../../lib/idempotency';
import { formatCurrency } from '@/lib/formatters';

export const useUsersCore = (
  addToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void,
  currentUser: User | null,
  setCurrentUser: (user: User | null) => void
) => {
  const [users, setUsers] = useState<User[]>([]);
  const [userStats, setUserStats] = useState<Record<string, UserStatsSummary>>({});

  const resolveUserId = (user: Partial<User> | null | undefined): string =>
    String(user?.id || user?._id || '').trim();

  const extractEnvelopeData = (payload: unknown): unknown => {
    if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
      return (payload as Record<string, unknown>).data;
    }
    return payload;
  };

  /**
   * Loads one server page of users. `append` adds the page to the current list
   * (Load more); otherwise the list is replaced. Errors propagate to the caller.
   */
  const fetchUsers = useCallback(async (
    query: UsersListQuery = {},
    options: { append?: boolean } = {}
  ): Promise<UsersListPage> => {
    const page = await usersApi.list(query);
    const list = page.users.map(u => ({
      ...u,
      id: String(u.id || u._id || '').trim(),
      _id: String(u._id || u.id || '').trim()
    }));
    if (options.append) {
      setUsers(prev => {
        const seen = new Set(prev.map(resolveUserId));
        return [...prev, ...list.filter(u => !seen.has(resolveUserId(u)))];
      });
      return { users: list, nextCursor: page.nextCursor };
    }
    setUsers(list);
    if (list.length === 0) {
      setUserStats({});
      return { users: list, nextCursor: page.nextCursor };
    }

    try {
      const statsPayload = await usersApi.listStats();
      const statsData = extractEnvelopeData(statsPayload);
      const statsList = Array.isArray((statsData as { stats?: unknown[] } | undefined)?.stats)
        ? (statsData as { stats: UserStatsSummary[] }).stats
        : Array.isArray(statsData)
          ? statsData as UserStatsSummary[]
          : [];
      const nextStats = statsList.reduce((acc: Record<string, UserStatsSummary>, stats: UserStatsSummary) => {
        if (!stats?.userId) return acc;
        acc[stats.userId] = {
          userId: stats.userId,
          orderCount: Number(stats.orderCount || 0),
          totalSpend: Number(stats.totalSpend || 0),
          lastOrderAt: stats.lastOrderAt ?? null
        };
        return acc;
      }, {});
      setUserStats(nextStats);
    } catch {
      // best-effort stats fetch
    }

    return { users: list, nextCursor: page.nextCursor };
  }, []);

  const fetchUserStats = useCallback(async (userId: string) => {
    try {
      const payload = await usersApi.getStats(userId);
      const data = extractEnvelopeData(payload);
      const stats = (data as { stats?: UserStatsSummary } | null | undefined)?.stats;
      if (stats?.userId) {
        const mapped: UserStatsSummary = {
          userId: stats.userId,
          orderCount: Number(stats.orderCount || 0),
          totalSpend: Number(stats.totalSpend || 0),
          lastOrderAt: stats.lastOrderAt ?? null
        };
        setUserStats(prev => ({ ...prev, [stats.userId]: mapped }));
        return mapped;
      }
    } catch {
      // best-effort stats fetch
    }
    return null;
  }, []);

  const updateUserProfile = useCallback(async (id: string, updates: Partial<User>) => {
    const payload = await usersApi.update(id, updates as Record<string, unknown>);
    const data = extractEnvelopeData(payload);
    const updatedRaw = (data as { user?: User } | undefined)?.user || (data as User);
    const updated = {
      ...updatedRaw,
      id: String(updatedRaw.id || updatedRaw._id || '').trim(),
      _id: String(updatedRaw._id || updatedRaw.id || '').trim()
    };
    const normalizedUpdatedId = resolveUserId(updated);
    const normalizedRequestedId = String(id || '').trim();
    setUsers(prev => prev.map((u) => {
      const currentId = resolveUserId(u);
      return currentId === normalizedRequestedId || (normalizedUpdatedId && currentId === normalizedUpdatedId)
        ? updated
        : u;
    }));
    addToast('USER UPDATED', 'success');
    const currentUserId = resolveUserId(currentUser);
    if (currentUserId && (currentUserId === normalizedRequestedId || currentUserId === normalizedUpdatedId)) {
      setCurrentUser(updated);
    }
    return updated;
  }, [addToast, currentUser, setCurrentUser]);

  /** Runs privacy deletion for the account; callers own confirmation and toasts. */
  const deleteUser = useCallback(async (id: string) => {
    const normalizedTargetId = String(id || '').trim();
    await usersApi.remove(normalizedTargetId, buildIdempotencyKey('admin-user-delete', [normalizedTargetId]));
    setUsers(prev => prev.filter(u => resolveUserId(u) !== normalizedTargetId));
    return true;
  }, []);

  const redeemPoints = useCallback(async (points: number) => {
    const uid = resolveUserId(currentUser);
    if (!uid) return;
    try {
      const response = await rewardsApi.redeemPoints({
        userId: uid,
        points: Math.abs(points),
        reason: 'customer_redeem_points'
      });
      const walletCredit = Number(response?.data?.walletCreditGranted || 0);
      const walletBalance = Number(response?.data?.walletBalance);
      const pointsBalance = Number(response?.data?.newBalance);
      if (currentUser) {
        setCurrentUser({
          ...currentUser,
          loyaltyPoints: Number.isFinite(pointsBalance) ? pointsBalance : currentUser.loyaltyPoints,
          creditBalance: Number.isFinite(walletBalance) ? walletBalance : currentUser.creditBalance
        });
      }
      if (walletCredit > 0) {
        addToast(`POINTS CONVERTED — ${formatCurrency(walletCredit)} wallet credit`, 'success');
      } else {
        addToast('Redeem did not apply wallet credit. Try again or contact support.', 'warning');
      }
    } catch (e: unknown) {
      addToast(e instanceof Error ? e.message : String(e || 'Redeem failed'), 'warning');
    }
  }, [addToast, currentUser, setCurrentUser]);

  return {
    users,
    setUsers,
    userStats,
    setUserStats,
    fetchUsers,
    fetchUserStats,
    updateUserProfile,
    deleteUser,
    redeemPoints
  };
};
